/**
 * The Inspector (SPEC §3, rule 4): the selected entity's header (name,
 * visibility, class tags) and one section per component, generated from the
 * component schemas by `inspectorModel`. It never names a component.
 *
 * With nothing selected it shows an overview of the open level instead
 * (LevelOverviewPanel): a portfolio's empty selection should still say what
 * you're looking at.
 *
 * With several entities selected it shows the components they share, a dash
 * where their values differ, and edits all of them, like Unreal's details
 * panel. The header then only counts them.
 *
 * With one entity selected, Add Component (at the end) and each section's
 * menu (Remove component) change which components it has, within the rules
 * the schemas declare (world/schema/componentRules), like Unreal and Unity.
 *
 * Every field is a prop widget connected through `useComponentPropBinding`,
 * so edits are `entity.setProperty` commands that apply live and coalesce
 * into one undo step per edit (AC 4).
 */
import { useMemo, useState } from "react";
import type { EntityRecord } from "../../world/entity";
import { useComponentPropBinding } from "../bindings/bindings";
import { useEditor } from "../EditorContext";
import { useEntities, useEntity, useSelection, useWorldSnapshot } from "../hooks/editorHooks";
import { menuPointFor, useContextMenu } from "../ui/ContextMenu";
import type { ContextMenuHandle } from "../ui/ContextMenu";
import { ChevronDownIcon, ChevronRightIcon, PlusIcon } from "../ui/icons";
import type { MenuItem } from "../ui/Menu";
import { addableComponents, whyCantRemove } from "../../world/schema/componentRules";
import { PropWidget } from "../widgets/PropWidget";
import { WidgetEnvironmentProvider } from "../widgets/WidgetEnvironment";
import type { WidgetEnvironment } from "../widgets/WidgetEnvironment";
import { InspectorHeader, MultiHeader } from "./InspectorHeader";
import { inspectorSections } from "./inspectorModel";
import type { InspectorRow, InspectorSection } from "./inspectorModel";
import { assetLookupIn, assetOptions } from "./assetOptions";
import { clipOptions } from "./clipOptions";
import { LevelOverviewPanel } from "./LevelOverviewPanel";
import { SkinField } from "./SkinField";
import { viewportColorTokens } from "./colorTokens";
import { useTourTarget } from "../tour/TourTargets";
import "./inspector.css";

export function InspectorPanel() {
  const { ids, active } = useSelection();
  const entities = useEntities(ids);
  const entity = useEntity(active);
  const environment = useWidgetEnvironment(entity);
  const tourTarget = useTourTarget("inspector");

  if (!entity || entities.length === 0) {
    return (
      // Nothing selected: the open level's overview, as an asset's details page reads.
      <div ref={tourTarget} className="kn-inspector kn-inspector--empty" data-testid="inspector">
        <LevelOverviewPanel />
      </div>
    );
  }

  return (
    // data-editor-field: Ctrl+Z in these fields is editor undo, not the input's own (A-18).
    <div ref={tourTarget} className="kn-inspector" data-testid="inspector" data-editor-field>
      {entities.length === 1 ? (
        <InspectorHeader entity={entity} />
      ) : (
        <MultiHeader entities={entities} active={entity} />
      )}
      <WidgetEnvironmentProvider value={environment}>
        {/* Keyed by selection: changing it remounts fields, ending their edit sessions. */}
        <Sections key={ids.join(",")} entities={entities} />
      </WidgetEnvironmentProvider>
    </div>
  );
}

/** The selected entities, as the fields see them. */
type Targets = readonly EntityRecord[];

function Sections({ entities }: { entities: Targets }) {
  const { components, content, dispatch } = useEditor();
  const menu = useContextMenu();
  const removeItem = useRemoveItem();
  const sections = inspectorSections(entities, components, assetLookupIn(content.index));
  // Adding and removing components edits one entity at a time.
  const [only] = entities.length === 1 ? entities : [];

  return (
    <>
      {sections.map((section) => (
        <ComponentSection
          key={section.type}
          entities={entities}
          section={section}
          onOptions={
            only
              ? (at) => {
                  menu.open(at, section.title, [removeItem(only, section.type)]);
                }
              : null
          }
          toggle={
            only && components.get(section.type)?.rules?.toggleable
              ? {
                  enabled: only.disabled?.has(section.type) !== true,
                  set: (enabled) => {
                    void dispatch({
                      type: "entity.setComponentEnabled",
                      payload: { entity: only.id, component: section.type, enabled },
                      origin: "inspector",
                    });
                  },
                }
              : null
          }
        />
      ))}
      {only && <AddComponentButton entity={only} openMenu={menu.open} />}
      {menu.element}
    </>
  );
}

/** Remove component, disabled when the schema rules keep it (required, or needed by another). */
function useRemoveItem() {
  const { components, world, dispatch } = useEditor();
  return (entity: EntityRecord, type: string): MenuItem => {
    const reason = whyCantRemove(components, world.getSnapshot().entities, entity.id, type);
    return {
      label: "Remove component",
      disabled: reason !== null,
      run: () => {
        void dispatch({
          type: "entity.removeComponent",
          payload: { entity: entity.id, component: type },
          origin: "inspector",
        });
      },
    };
  };
}

/** The components the entity can take now, as a menu, like Unreal's Add Component. */
function AddComponentButton({
  entity,
  openMenu,
}: {
  entity: EntityRecord;
  openMenu: ContextMenuHandle["open"];
}) {
  const { components, world, dispatch } = useEditor();

  const open = (at: { x: number; y: number }) => {
    const addable = addableComponents(components, world.getSnapshot().entities, entity.id);
    openMenu(
      at,
      "Add Component",
      addable.map((schema) => ({
        label: schema.category,
        run: () => {
          void dispatch({
            type: "entity.addComponent",
            payload: { entity: entity.id, component: schema.type },
            origin: "inspector",
          });
        },
      })),
    );
  };

  return (
    <div className="kn-inspector__add">
      <button
        type="button"
        className="kn-button"
        aria-haspopup="menu"
        onClick={(event) => {
          open(menuPointFor(event.currentTarget));
        }}
      >
        <PlusIcon size={12} />
        Add Component
      </button>
    </div>
  );
}

// ─── Sections and rows ───────────────────────────────────────────────────

/** A section's enabled checkbox: its component's state and the command that changes it. */
interface SectionToggle {
  readonly enabled: boolean;
  readonly set: (enabled: boolean) => void;
}

function ComponentSection({
  entities,
  section,
  onOptions,
  toggle,
}: {
  entities: Targets;
  section: InspectorSection;
  /** Opens the section's menu; null when it has none (several entities selected). */
  onOptions: ((at: { x: number; y: number }) => void) | null;
  /** The enabled checkbox, for components that can be turned off (one entity selected). */
  toggle: SectionToggle | null;
}) {
  const [open, setOpen] = useState(true);
  const bodyId = `inspector-${section.type}`;
  const Chevron = open ? ChevronDownIcon : ChevronRightIcon;

  return (
    <section
      className="kn-inspector__section"
      aria-label={section.title}
      {...(toggle && !toggle.enabled && { "data-disabled": true })}
    >
      <h3 className="kn-inspector__section-title">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => {
            setOpen(!open);
          }}
        >
          <Chevron size={10} />
          {section.title}
        </button>
        {toggle && (
          <input
            type="checkbox"
            className="kn-checkbox kn-inspector__section-enabled"
            aria-label={`${section.title} enabled`}
            title={toggle.enabled ? "Turn off" : "Turn on"}
            checked={toggle.enabled}
            onChange={(event) => {
              toggle.set(event.target.checked);
            }}
          />
        )}
        {onOptions && (
          <button
            type="button"
            className="kn-inspector__section-options"
            aria-label={`${section.title} options`}
            title="Component options"
            aria-haspopup="menu"
            onClick={(event) => {
              onOptions(menuPointFor(event.currentTarget));
            }}
          >
            ⋯
          </button>
        )}
      </h3>
      {open && (
        <div id={bodyId} className="kn-inspector__rows">
          {section.rows.map((row) => (
            <InspectorField
              key={rowKey(row)}
              entities={entities}
              component={section.type}
              row={row}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function InspectorField({
  entities,
  component,
  row,
}: {
  entities: Targets;
  component: string;
  row: InspectorRow;
}) {
  // Ids only need to be unique on the page; the first entity stands for the selection.
  const id = `${entities[0]?.id ?? ""}-${component}-${rowKey(row)}`;
  return (
    <div className="kn-inspector__row">
      <label className="kn-inspector__label" htmlFor={id}>
        {row.label}
      </label>
      {row.kind === "range" ? (
        <span className="kn-inspector__range">
          <BoundWidget
            id={id}
            entities={entities}
            component={component}
            propKey={row.from}
            prop={row.fromProp}
            label={row.label}
          />
          <span className="kn-inspector__range-arrow" aria-hidden="true">
            -
          </span>
          <BoundWidget
            id={`${id}-to`}
            entities={entities}
            component={component}
            propKey={row.to}
            prop={row.toProp}
            label={row.toProp.label ?? row.to}
          />
        </span>
      ) : row.kind === "skin" ? (
        <SkinField
          id={id}
          entities={entities}
          component={component}
          propKey={row.key}
          set={row.set}
        />
      ) : (
        <BoundWidget
          id={id}
          entities={entities}
          component={component}
          propKey={row.key}
          prop={row.prop}
          label={row.label}
        />
      )}
    </div>
  );
}

/** Unique within a section: a range by its first prop, a Skin row apart from the prop it edits. */
function rowKey(row: InspectorRow): string {
  if (row.kind === "range") return row.from;
  return row.kind === "skin" ? `${row.key}-skin` : row.key;
}

interface BoundWidgetProps {
  readonly id: string;
  readonly entities: Targets;
  readonly component: string;
  readonly propKey: string;
  readonly prop: Extract<InspectorRow, { kind: "prop" }>["prop"];
  readonly label: string;
}

/** A prop widget connected to `entity.setProperty` on each selected entity (D-4). */
function BoundWidget({ id, entities, component, propKey, prop, label }: BoundWidgetProps) {
  const binding = useComponentPropBinding(
    entities.map((entity) => entity.id),
    component,
    propKey,
  );
  return (
    <PropWidget
      id={id}
      label={label}
      prop={prop}
      value={binding.value}
      onChange={binding.onChange}
      onCommit={binding.onCommit}
      error={binding.error}
    />
  );
}

// ─── Widget environment ──────────────────────────────────────────────────

/**
 * What the Inspector's widgets can offer: entities from the open level, assets
 * from the content index, and the clips of the edited entity's model.
 */
function useWidgetEnvironment(entity: EntityRecord | undefined): WidgetEnvironment {
  const snapshot = useWorldSnapshot();
  const { content } = useEditor();
  return useMemo(
    () => ({
      entityOptions: (requires) =>
        [...snapshot.entities.values()]
          .filter((entity) => requires === undefined || entity.components.has(requires))
          .map((entity) => ({ value: entity.id, label: entity.name })),
      assetOptions: (kinds) => assetOptions(content.index, kinds, (path) => content.url(path)),
      clipOptions: (source) =>
        clipOptions(entity?.components ?? new Map(), source, content.index.assets),
      colorTokens: viewportColorTokens(),
    }),
    [snapshot, content, entity],
  );
}
